import React from 'react';
import styled from 'styled-components';
import { Signal, useComputed } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { ChatAreaProps, ChatArea } from '@/features/ai-agents/chat/chat-area';
import { useLoggedInAccount } from '@/features/account-context';
import {
  ChatSessionContextProps,
  ChatSessionContextProvider,
} from '@/features/ai-agents/chat/context';
import { SessionListEntry } from '@/features/ai-agents/shared/use-ai-metadata-api';
import {
  AiChatMessage,
  AiChatReference,
  ContextualAttachment,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';
import { AiPromptMessage } from '@/features/editor/context/use-device-preview';

export interface AiAgentChatProps extends ChatAreaProps {
  agentId: string;
  agentType: `agent` | `team` | `workflow`;

  initialSessionId?: string;
  userIdOverride?: string;

  onNewMessageSent?: (message: AiChatMessage) => void;
  onNewSession?: (session: SessionListEntry) => void;
  onNewReply?: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId: string,
  ) => void;
  onSessionLoaded?: (session: AiChatMessage[]) => void;

  padding?: string;
  inputBottom?: string;
  inputLeft?: string;
  inputRight?: string;

  onLoadingChanged?: (loading: boolean) => void;
  sessionData?: Record<string, any>;
  starters?: AiConversationStarter;
  contextualAttachments?: Signal<ContextualAttachment[]>;
  references?: Signal<AiChatReference[]>;
  onRemoveReference?: (ref: AiChatReference) => void;
  onEditAttachment?: (
    oldAttachment: ContextualAttachment,
    newSelector: string,
  ) => void;
  onAgentSelect?: (agent: SelectedAgent) => void;
  placeholder?: string;
  initialMessage?: AiPromptMessage;
  emptyMessage?: string;
}

export function AiAgentChat({
  agentId,
  agentType,
  initialSessionId,
  onNewMessageSent,
  onNewSession,
  onNewReply,
  onSessionLoaded,
  userIdOverride,
  showTools,
  showReasoning,
  enableExamples,
  enableVoiceInput,
  enableFilesUpload,
  enableAgentSelection,
  padding,
  inputBottom,
  inputLeft,
  inputRight,
  onLoadingChanged,
  sessionData,
  starters,
  contextualAttachments,
  onEditAttachment,
  onAgentSelect,
  placeholder,
  initialMessage,
  emptyMessage,
  onLeaveAnchor,
}: AiAgentChatProps) {
  useSignals();
  const userId = useLoggedInAccount()?.store?.email;

  const props = useComputed<ChatSessionContextProps>(() => ({
    agent: {
      id: agentId,
      type: agentType,
    },
    initialSessionId,
    userId: userIdOverride || userId,
    onNewMessageSent,
    onNewSession,
    onNewReply,
    onSessionLoaded,
    onLoadingChanged,
    sessionData,
  }));

  return (
    <ChatSessionContextProvider props={props}>
      <Wrapper style={{ padding: padding || `0` }}>
        <ChatArea
          showTools={showTools}
          showReasoning={showReasoning}
          enableExamples={enableExamples}
          enableVoiceInput={enableVoiceInput}
          enableFilesUpload={enableFilesUpload}
          enableAgentSelection={enableAgentSelection}
          inputBottom={inputBottom}
          inputLeft={inputLeft}
          inputRight={inputRight}
          starters={starters}
          contextualAttachments={contextualAttachments}
          onEditAttachment={onEditAttachment}
          onAgentSelect={onAgentSelect}
          placeholder={placeholder}
          initialMessage={initialMessage}
          emptyMessage={emptyMessage}
          onLeaveAnchor={onLeaveAnchor}
        />
      </Wrapper>
    </ChatSessionContextProvider>
  );
}

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  max-height: 100%;
`;
